'use client';

/**
 * API namespace `geo`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L9266-L9314).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const geo = {
  /**
   * GET /api/geo/countries — list active countries for KYC dropdown 1.
   *
   * Returns GeoLocationOption[] (geoLocationId + name + code only —
   * minimal payload for dropdown, no timestamps/childrenCount/status).
   */
  async getCountries(signal?: AbortSignal): Promise<GeoLocationOption[]> {
    return apiFetch<GeoLocationOption[]>('/geo/countries', { signal });
  },
  /**
   * GET /api/geo/states/:countryId — list active states of a country.
   *
   * Path param: countryId (UUID v4 — validated by backend Zod schema
   * geoLocationIdParamSchema).
   *
   * Returns empty array if country doesn't exist or is inactive (no 404
   * — graceful degradation for the dropdown).
   */
  async getStates(countryId: string, signal?: AbortSignal): Promise<GeoLocationOption[]> {
    return apiFetch<GeoLocationOption[]>(`/geo/states/${countryId}`, { signal });
  },
  /**
   * GET /api/geo/cities/:stateId — list active cities of a state.
   *
   * Path param: stateId (UUID v4).
   *
   * Returns empty array if state doesn't exist or is inactive (no 404).
   */
  async getCities(stateId: string, signal?: AbortSignal): Promise<GeoLocationOption[]> {
    return apiFetch<GeoLocationOption[]>(`/geo/cities/${stateId}`, { signal });
  },
};
